import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { NavbarComponent } from '@app/layouts/sidebar/sidebar.component';
import { LanguageService } from '@app/services/language.service';
import { JwtService } from '@app/core/auth';
import { SiteService } from '@app/features/sites/services/site.service';
import { ErrorBannerComponent } from '@shared/components/error-banner.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, NavbarComponent, ErrorBannerComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly languageService = inject(LanguageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly jwt = inject(JwtService);
  private readonly siteService = inject(SiteService);

  readonly showLayout = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map((e: NavigationEnd) => !e.url.includes('/unauthorized')),
      startWith(!this.router.url.includes('/unauthorized')),
    ),
    { initialValue: !this.router.url.includes('/unauthorized') },
  );

  ngOnInit(): void {
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        const lang = params['lang'];
        if (lang) this.languageService.setLanguage(lang);
      });

    // Bootstrap sites so the navbar's site-switcher is always populated,
    // regardless of which route the user lands on first.
    this.jwt.token$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(token => {
        if (token) this.siteService.loadSites();
      });

    this.siteService.sites$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(sites => {
        if (sites.length && !this.siteService.getCurrentSelectedSite()) {
          this.siteService.hydrateFromStorage(sites);
          if (!this.siteService.getCurrentSelectedSite()) {
            this.siteService.selectSite(sites[0]);
          }
        }
      });
  }
}
